import React from 'react'
import { formatLocal } from 'utils/date'
import axios from 'axios'

import { translate } from 'locales'
import { TextArea, Checkbox, Radio } from 'components'
import useStorage from 'hooks/storageSync'
import useFetch from 'hooks/useFetch'
import getAge from 'utils/getAge'
import TagSelectorNutri from './TagSelectorNutri/TagSelectorNutri'
import { alertSuccess, alertDanger } from 'utils/toast'

// Interfaces
import { IComponentProps } from './interfaces'

// utils
import {
  reasonConsultation,
  pathologies,
  waterConsumption,
  intestinalHabit,
  fraquency,
  sleep,
  youAre,
} from './utils'

import * as S from './styles'

const updatedReasonConsultation = reasonConsultation.filter(
  (reason) => reason !== 'Retorno'
)
const firstReasonColumn = [
  ...updatedReasonConsultation.slice(
    0,
    Math.ceil(updatedReasonConsultation.length / 2) - 1
  ),
  'Retorno',
]
const secondReasonColumn = updatedReasonConsultation.slice(
  Math.ceil(updatedReasonConsultation.length / 2) - 1
)

const updatedPathologies = pathologies.filter(
  (pathology) => pathology !== 'Nenhuma'
)
const firstPathologyColumn = [
  ...updatedPathologies.slice(0, Math.ceil(updatedPathologies.length / 2) - 1),
  'Nenhuma',
]
const secondPathologyColumn = updatedPathologies.slice(
  Math.ceil(updatedPathologies.length / 2) - 1
)

const updatedFraquency = [...fraquency, 'Diariamente']
const updatedSleep = [...sleep, 'Dorme bem']

const AnamneseNutri: React.FunctionComponent<IComponentProps> = ({
  doctorName,
  scheduleUUID,
  planSent,
}) => {
  const [storage, setStorage, setAllStorage]: any = useStorage(
    scheduleUUID,
    'nutritional_anamnese'
  )
  const [isGeneratingPlan, setIsGeneratingPlan] = React.useState(false)

  const { data: appointment } = useFetch({
    url: `/schedulings/ms/v1/${scheduleUUID}/doctor`,
    condition: !!scheduleUUID,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  const { data: userData } = useFetch({
    url: `accounts/ms/v1/user/${appointment?.user_id}/me`,
    condition: !!appointment?.user_id,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  const { data: userMedical } = useFetch({
    url: `accounts/v1/medical-record/user/${appointment?.user_id}`,
    condition: !!appointment?.user_id,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  const hasFilledAnamnese = React.useMemo(() => {
    const data = storage?.nutritional_anamnese
    if (!data) return false
    return Object.values(data).some((value: any) => {
      if (value === null || value === undefined) return false
      if (typeof value === 'string') return value.trim().length > 0
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === 'boolean') return true
      return false
    })
  }, [storage?.nutritional_anamnese])

  const handleChange = (nome: any, valor: any) => {
    const newItens = storage?.nutritional_anamnese?.[nome] || []

    if (newItens.includes(valor)) {
      const filtered = newItens?.filter((item: any) => item !== valor)
      const data = {
        ...storage,
        nutritional_anamnese: {
          ...storage?.nutritional_anamnese,
          [nome]: filtered,
        },
      }
      setAllStorage(data)
    } else {
      const data = {
        ...storage,
        nutritional_anamnese: {
          ...storage?.nutritional_anamnese,
          [nome]: [...newItens, valor],
        },
      }
      setAllStorage(data)
    }
  }

  const handleWeightChange = (key: string, e: any) => {
    let { value } = e.target

    value = value.replace(/[^\d.]/g, '')

    const match = value.match(/^\d{0,3}(\.\d{0,2})?$/)
    if (match) {
      setStorage(key, value)
    }
  }

  const handleHeightChange = (e: any) => {
    let { value } = e.target
    value = value.replace(/[^\d.]/g, '')
    const match = value.match(/^[0-2](\.\d{0,2})?$/)
    if (match) {
      const numValue = parseFloat(value)
      if (numValue >= 0.5 && numValue <= 2.5) {
        setStorage('height_m', value)
      } else if (value === '' || value === '0' || value === '0.') {
        setStorage('height_m', value)
      }
    }
  }

  const handleAgeChange = (e: any) => {
    let { value } = e.target
    value = value.replace(/[^\d]/g, '')
    const match = value.match(/^\d{0,3}$/)
    if (match) {
      setStorage('age', value)
    }
  }

  const handleChangeText = (key: any, e: any) => {
    setStorage(key, e.target.value)
  }

  const handleChangeCheckBox = (e: any, value: any) => {
    setStorage(e, value)
  }

  const normalizeDietType = (diet: string): string => {
    if (!diet) return 'onivora'

    const normalized = diet
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()

    const dietMap: any = {
      onivora: 'onivora',
      onívora: 'onivora',
      vegetariana: 'vegetariana',
      vegana: 'vegana',
      vegan: 'vegana',
      ovolactovegetariana: 'ovolactovegetariana',
      ovolacto: 'ovolactovegetariana',
      outros: 'outros',
      outras: 'outros',
    }

    return dietMap[normalized] || normalized
  }

  const generateMealPlan = async () => {
    setIsGeneratingPlan(true)

    alertSuccess('Gerando plano alimentar... Isso pode levar alguns segundos.')

    try {
      const token = window.localStorage.getItem('token')

      if (!token) {
        alertDanger('Token de autenticação não encontrado')
        return
      }

      const anamneseData = storage?.nutritional_anamnese

      const age =
        parseInt(anamneseData?.age) || getAge(userData?.birthdate || '') || 0

      if (!age || age <= 0) {
        alertDanger('Idade é obrigatória')
        setIsGeneratingPlan(false)
        return
      }

      if (!anamneseData?.current_weight) {
        alertDanger('Peso atual é obrigatório')
        setIsGeneratingPlan(false)
        return
      }

      const heightM =
        anamneseData?.height_m ||
        (userMedical?.height ? Number(userMedical.height / 100).toFixed(2) : '')

      if (!heightM) {
        alertDanger('Altura é obrigatória')
        setIsGeneratingPlan(false)
        return
      }

      if (!anamneseData?.diet) {
        alertDanger('Tipo de dieta é obrigatório')
        setIsGeneratingPlan(false)
        return
      }

      const genderMap: any = {
        male: 'M',
        female: 'F',
        other: 'O',
      }
      const gender = genderMap[userData?.gender] || 'M'

      const primaryObjectiveMap: any = {
        Emagrecimento: 'emagrecimento',
        Ganho: 'ganho_de_peso',
        'Ganho de peso': 'ganho_de_peso',
        'Ganho de massa': 'ganho_de_massa',
        'Manutenção de peso': 'manutencao',
        'Melhoria da composição corporal': 'melhoria_composicao',
        'Iniciar um hábito de alimentar mais saudável': 'habito_saudavel',
      }

      const primaryObjective = anamneseData?.reason?.[0]
        ? primaryObjectiveMap[anamneseData.reason[0]] || 'emagrecimento'
        : 'emagrecimento'

      const supplementsList = anamneseData?.supplements || []
      const supplementsFormatted = supplementsList.map((supp: string) => ({
        name: supp,
        notes: anamneseData?.supplements_comments || '',
      }))

      const allergies: string[] = []
      const intolerances: string[] = []

      if (
        anamneseData?.allergies === 'Sim' &&
        anamneseData?.allergies_comments
      ) {
        const allergiesText = anamneseData.allergies_comments
        const allergiesList = allergiesText
          .split(/[,\n]/)
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
        allergies.push(...allergiesList)
      }

      if (
        anamneseData?.intolerances === 'Sim' &&
        anamneseData?.intolerances_comments
      ) {
        const intolerancesText = anamneseData.intolerances_comments
        const intolerancesList = intolerancesText
          .split(/[,\n]/)
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
        intolerances.push(...intolerancesList)
      }

      const aversions: string[] = []
      if (anamneseData?.food_aversion) {
        const aversionsText = anamneseData.food_aversion
        const aversionsList = aversionsText
          .split(/[,\n]/)
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
        aversions.push(...aversionsList)
      }

      const preferences: string[] = []
      if (anamneseData?.preferences) {
        const prefsText = anamneseData.preferences
        const prefsList = prefsText
          .split(/[,\n]/)
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
        preferences.push(...prefsList)
      }

      const sleepText =
        anamneseData?.sleep?.join(', ') || anamneseData?.sleep_comments || ''

      const bowelHabits = {
        frequency: anamneseData?.frequency_intestinal_habit || '',
        consistency: anamneseData?.consistency_intestinal_habit || '',
      }

      const currentEatingRoutine = {
        routine_breakfast: anamneseData?.routine_breakfast || '',
        routine_morning_snack: anamneseData?.routine_morning_snack || '',
        routine_lunch: anamneseData?.routine_lunch || '',
        routine_afternoon_snack: anamneseData?.routine_afternoon_snack || '',
        routine_dinner: anamneseData?.routine_dinner || '',
        routine_supper: anamneseData?.routine_supper || '',
        routine_weekends: anamneseData?.routine_weekends || '',
      }

      const userStorage: any = window.localStorage.getItem('@StarBem:user')
      const doctorData = userStorage ? JSON.parse(userStorage) : null
      let userId = doctorData?.id || appointment?.doctor_id

      if (userId) {
        userId = String(userId).trim()
      }

      if (
        !userId ||
        userId === '' ||
        userId === 'undefined' ||
        userId === 'null'
      ) {
        alertDanger('ID do médico não encontrado. Faça login novamente.')
        setIsGeneratingPlan(false)
        return
      }

      const region =
        appointment?.user_country === 'BR' ? 'sudeste_br' : 'sudeste_br'

      const apiPayload: any = {
        request_metadata: {
          patient_id: appointment?.user_id || scheduleUUID,
          doctor_id: userId,
          appointment_id: scheduleUUID,
          provider_id: process.env.REACT_APP_MEAL_PLAN_PROVIDER_ID || '',
          region: region,
          request_type: 'plan_builder',
        },
        patient_profile: {
          full_name: userData?.name || userData?.social_name || '',
          gender: gender,
          age: age,
          current_weight_kg: parseFloat(anamneseData?.current_weight) || 0,
          old_weight_kg: parseFloat(anamneseData?.old_weight) || 0,
          height_m: parseFloat(heightM) || 0,
        },
        nutritional_plan_goals: {
          primary_objective: primaryObjective,
          reasons: anamneseData?.reason || [],
          flexibility_notes: anamneseData?.flexibility_notes || '',
          goal_notes: anamneseData?.reason_comments || '',
        },
        medical_and_supplements: {
          pathologies: anamneseData?.pathologies || [],
          medications: {
            uses: anamneseData?.medicines || false,
            which_ones: anamneseData?.medicines_comments
              ? [anamneseData.medicines_comments]
              : [],
          },
          supplements: supplementsFormatted,
        },
        dietary_restrictions_and_habits: {
          diet_type: normalizeDietType(anamneseData?.diet || ''),
          allergies: allergies,
          intolerances: intolerances,
          aversions: aversions,
          preferences: preferences,
          water_intake: anamneseData?.water_consumption || '',
          other_considerations:
            anamneseData?.considerations ||
            anamneseData?.extra_information ||
            '',
        },
        routine_and_activity: {
          physical_activity: {
            practices: anamneseData?.practice_activity || false,
            type: anamneseData?.practice_activity_type || '',
            frequency_schedule: anamneseData?.practice_activity_comments || '',
          },
          sleep: sleepText,
          bowel_habits: bowelHabits,
        },
        current_eating_routine: currentEatingRoutine,
      }

      if (
        !apiPayload.request_metadata.doctor_id ||
        apiPayload.request_metadata.doctor_id === ''
      ) {
        alertDanger('ID do médico não encontrado. Faça login novamente.')
        setIsGeneratingPlan(false)
        return
      }

      if (!process.env.REACT_APP_MEAL_PLAN_BASE_URL) {
        alertDanger('URL da API não configurada.')
        setIsGeneratingPlan(false)
        return
      }

      if (!process.env.REACT_APP_MEAL_PLAN_API_KEY) {
        alertDanger('API Key não configurada.')
        setIsGeneratingPlan(false)
        return
      }

      const response = await axios.post(
        process.env.REACT_APP_MEAL_PLAN_BASE_URL,
        apiPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_MEAL_PLAN_API_KEY,
          },
        }
      )

      let mealPlanData

      try {
        mealPlanData =
          typeof response.data === 'string'
            ? JSON.parse(response.data)
            : response.data
      } catch {
        throw new Error('Erro ao processar resposta da API: dados inválidos')
      }

      if (!mealPlanData) {
        throw new Error('Resposta vazia da API')
      }

      if (mealPlanData.errorMessage || mealPlanData.errorType) {
        const backendMsg = String(mealPlanData.errorMessage || '')
        if (backendMsg.includes('BEDROCK_MODEL_ID')) {
          alertDanger(
            'Falha na configuração do modelo de IA. Contate o suporte (BEDROCK_MODEL_ID ausente).'
          )
        } else {
          alertDanger(
            mealPlanData.errorMessage ||
              mealPlanData.error ||
              'Erro ao gerar plano alimentar. Tente novamente.'
          )
        }
        setIsGeneratingPlan(false)
        return
      }

      if (mealPlanData.success === false) {
        let errorMsg =
          mealPlanData.message ||
          mealPlanData.error ||
          mealPlanData.errorMessage ||
          'Erro desconhecido'

        if (
          mealPlanData.validation_errors &&
          Array.isArray(mealPlanData.validation_errors)
        ) {
          const validationDetails = mealPlanData.validation_errors.join('; ')
          errorMsg = `${errorMsg}. Detalhes: ${validationDetails}`
        }

        throw new Error(errorMsg)
      }

      const updatedStorage = {
        ...storage,
        meal_plan: mealPlanData.plan || mealPlanData,
        meal_plan_metadata: mealPlanData.metadata || null,
        generate_plan_requested: true,
        plan_generated_at: new Date().toISOString(),
        plan_data_structure: 'json_structured',
        plan_patient_id: scheduleUUID,
        menu_modified: false,
      }
      setAllStorage(updatedStorage)

      alertSuccess('Plano alimentar gerado com sucesso!')
    } catch (error: any) {
      let errorMessage = 'Erro ao gerar plano alimentar. Tente novamente.'

      if (error?.response) {
        const status = error.response.status
        const data = error.response.data

        if (status === 401) {
          errorMessage = 'Token de autenticação inválido ou expirado'
        } else if (status === 400) {
          const baseMessage =
            data?.message ||
            data?.error ||
            'Dados inválidos enviados para a API'

          if (
            data?.validation_errors &&
            Array.isArray(data.validation_errors)
          ) {
            const validationDetails = data.validation_errors.join('; ')
            errorMessage = `${baseMessage}. Detalhes: ${validationDetails}`
          } else {
            errorMessage = baseMessage
          }
        } else if (status === 500) {
          errorMessage =
            data?.message ||
            data?.error ||
            'Erro interno do servidor. Tente novamente mais tarde.'
        } else if (data?.message || data?.error) {
          const baseMessage = data.message || data.error
          if (
            data?.validation_errors &&
            Array.isArray(data.validation_errors)
          ) {
            const validationDetails = data.validation_errors.join('; ')
            errorMessage = `${baseMessage}. Detalhes: ${validationDetails}`
          } else {
            errorMessage = baseMessage
          }
        } else {
          errorMessage = `Erro ${status}: ${error.response.statusText || 'Erro desconhecido'}`
        }
      } else if (error?.message) {
        errorMessage = error.message
      }

      alertDanger(errorMessage)
    } finally {
      setIsGeneratingPlan(false)
    }
  }

  return (
    <S.SectionContent>
      <span>{formatLocal(new Date(), 'EEEE, dd/MM/yyyy')}</span>
      <p>{`${translate('medicalRecord.by')} ${doctorName}`}</p>
      <S.Div>
        <h3>Dados do Paciente:</h3>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <div
            className="subsection-item"
            style={{ flex: 1, minWidth: '200px' }}
          >
            <h4>
              Idade: <span style={{ color: '#FF7A00' }}>*</span>
            </h4>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name="age"
                value={
                  storage?.nutritional_anamnese?.age ||
                  (userData?.birthdate
                    ? getAge(userData.birthdate).toString()
                    : '')
                }
                placeholder="Digite a idade"
                onChange={handleAgeChange}
                style={{
                  borderRadius: '5px',
                  padding: '10px',
                  border: '1px solid #ccc',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '30px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                anos
              </span>
            </div>
          </div>
          <div
            className="subsection-item"
            style={{ flex: 1, minWidth: '200px' }}
          >
            <h4>Peso antigo:</h4>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name="old_weight"
                value={storage?.nutritional_anamnese?.old_weight || ''}
                placeholder="Digite o peso antigo"
                onChange={(e: any) => handleWeightChange('old_weight', e)}
                style={{
                  borderRadius: '5px',
                  padding: '10px',
                  border: '1px solid #ccc',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '30px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                kg
              </span>
            </div>
          </div>
          <div
            className="subsection-item"
            style={{ flex: 1, minWidth: '200px' }}
          >
            <h4>
              Peso atual: <span style={{ color: '#FF7A00' }}>*</span>
            </h4>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name="current_weight"
                value={storage?.nutritional_anamnese?.current_weight || ''}
                placeholder="Digite o peso atual"
                onChange={(e: any) => handleWeightChange('current_weight', e)}
                style={{
                  borderRadius: '5px',
                  padding: '10px',
                  border: '1px solid #ccc',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '30px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                kg
              </span>
            </div>
          </div>
          <div
            className="subsection-item"
            style={{ flex: 1, minWidth: '200px' }}
          >
            <h4>
              Altura (m): <span style={{ color: '#FF7A00' }}>*</span>
            </h4>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name="height_m"
                value={
                  storage?.nutritional_anamnese?.height_m ||
                  (userMedical?.height
                    ? Number(userMedical.height / 100).toFixed(2)
                    : '')
                }
                placeholder="Ex: 1.75"
                onChange={handleHeightChange}
                style={{
                  borderRadius: '5px',
                  padding: '10px',
                  border: '1px solid #ccc',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '30px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                m
              </span>
            </div>
          </div>
        </div>

        <h3 style={{ marginBottom: '10px', marginTop: '20px' }}>
          {translate('medicalRecord.reason')}
        </h3>
        <div style={{ marginTop: '10px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ flex: '1 1 calc(50% - 10px)' }}>
              {firstReasonColumn.map((reason) => (
                <div key={reason} style={{ marginBottom: '0.25rem' }}>
                  <Checkbox
                    label={reason}
                    name="reason"
                    id={reason}
                    checked={storage?.nutritional_anamnese?.reason?.includes(
                      reason
                    )}
                    onChange={(e) => handleChange(e.target.name, reason)}
                  />
                </div>
              ))}
            </div>
            <div style={{ flex: '1 1 calc(50% - 10px)' }}>
              {secondReasonColumn.map((reason) => (
                <div key={reason} style={{ marginBottom: '0.25rem' }}>
                  <Checkbox
                    label={reason}
                    name="reason"
                    id={reason}
                    checked={storage?.nutritional_anamnese?.reason?.includes(
                      reason
                    )}
                    onChange={(e) => handleChange(e.target.name, reason)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <h4 style={{ marginBottom: '5px', marginTop: '20px' }}>
          {translate('nutritionalReason.comments')}:
        </h4>
        <TextArea
          name="reason_comments"
          value={storage?.nutritional_anamnese?.reason_comments}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('reason_comments', e)}
          wrap="hard"
        />

        <h3>{translate('medicalRecord.pathologies')}</h3>
        <div style={{ marginTop: '10px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ flex: '1 1 calc(50% - 10px)' }}>
              {firstPathologyColumn.map((patologia) => (
                <div key={patologia} style={{ marginBottom: '0.25rem' }}>
                  <Checkbox
                    label={patologia}
                    name="pathologies"
                    id={patologia}
                    checked={storage?.nutritional_anamnese?.pathologies?.includes(
                      patologia
                    )}
                    onChange={(e) => handleChange(e.target.name, patologia)}
                  />
                </div>
              ))}
            </div>
            <div style={{ flex: '1 1 calc(50% - 10px)' }}>
              {secondPathologyColumn.map((patologia) => (
                <div key={patologia} style={{ marginBottom: '0.25rem' }}>
                  <Checkbox
                    label={patologia}
                    name="pathologies"
                    id={patologia}
                    checked={storage?.nutritional_anamnese?.pathologies?.includes(
                      patologia
                    )}
                    onChange={(e) => handleChange(e.target.name, patologia)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <h4 style={{ marginBottom: '5px', marginTop: '20px' }}>
          {translate('nutritionalReason.comments')}:
        </h4>
        <TextArea
          name="pathologies_comments"
          value={storage?.nutritional_anamnese?.pathologies_comments}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('pathologies_comments', e)}
          wrap="hard"
        />
        <h3>{translate('nutritionalReason.medicines')}</h3>
        <div style={{ marginTop: '10px', marginBottom: '10px' }}>
          <Radio
            label="Sim"
            name="medicines"
            checked={storage?.nutritional_anamnese?.medicines === true}
            onChange={() => handleChangeCheckBox('medicines', true)}
          />
          <Radio
            label="Não"
            name="medicines"
            checked={storage?.nutritional_anamnese?.medicines === false}
            onChange={() => handleChangeCheckBox('medicines', false)}
          />
        </div>
        <h4>{translate('nutritionalReason.which')}:</h4>
        <TextArea
          name="medicines_comments"
          value={storage?.nutritional_anamnese?.medicines_comments}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('medicines_comments', e)}
          wrap="hard"
        />
        <h3>{translate('nutritionalReason.supplements')}:</h3>
        <TagSelectorNutri scheduleUUID={scheduleUUID} />
        <h3>{translate('nutritionalReason.praticePhysicalActivity')}:</h3>
        <div style={{ marginTop: '10px', marginBottom: '10px' }}>
          <Radio
            label="Sim"
            name="practice_activity"
            checked={storage?.nutritional_anamnese?.practice_activity === true}
            onChange={() => handleChangeCheckBox('practice_activity', true)}
          />
          <Radio
            label="Não"
            name="practice_activity"
            checked={storage?.nutritional_anamnese?.practice_activity === false}
            onChange={() => handleChangeCheckBox('practice_activity', false)}
          />
        </div>
        <h4>{translate('nutritionalReason.frequencyAndSchedule')}</h4>
        <TextArea
          name="practice_activity_comments"
          value={storage?.nutritional_anamnese?.practice_activity_comments}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) =>
            handleChangeText('practice_activity_comments', e)
          }
          wrap="hard"
        />
        <h3>{translate('nutritionalReason.waterConsumption')}:</h3>
        <div style={{ marginTop: '10px' }}>
          <div style={{ marginBottom: '1rem' }}>
            {waterConsumption?.map((item) => (
              <Radio
                key={`inline-${item}`}
                label={item}
                name="water_consumption"
                id={`inline-water_consumption-${item}`}
                checked={
                  storage?.nutritional_anamnese?.water_consumption === item
                }
                onChange={() => handleChangeCheckBox('water_consumption', item)}
              />
            ))}
          </div>
        </div>
        <h4>{translate('nutritionalReason.comments')}:</h4>
        <TextArea
          name="water_consumption_comments"
          value={storage?.nutritional_anamnese?.water_consumption_comments}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) =>
            handleChangeText('water_consumption_comments', e)
          }
          wrap="hard"
        />
        <h3>{translate('nutritionalReason.intestinalHabit')}</h3>
        <h4>{translate('nutritionalReason.frequency')}:</h4>
        <div style={{ marginTop: '10px' }}>
          <div style={{ marginBottom: '1rem' }}>
            {updatedFraquency.map((item) => (
              <Radio
                key={`inline-${item}`}
                label={item}
                name="frequency_intestinal_habit"
                id={`inline-frequency_intestinal_habit-${item}`}
                checked={
                  storage?.nutritional_anamnese?.frequency_intestinal_habit ===
                  item
                }
                onChange={() =>
                  handleChangeCheckBox('frequency_intestinal_habit', item)
                }
              />
            ))}
          </div>
        </div>
        <h4>{translate('nutritionalReason.consistency')}</h4>
        <div style={{ marginTop: '10px' }}>
          <div style={{ marginBottom: '1rem' }}>
            {intestinalHabit?.map((item) => (
              <Radio
                key={`inline-${item}`}
                label={item}
                name="consistency_intestinal_habit"
                id={`inline-consistency_intestinal_habit-${item}`}
                checked={
                  storage?.nutritional_anamnese
                    ?.consistency_intestinal_habit === item
                }
                onChange={() =>
                  handleChangeCheckBox('consistency_intestinal_habit', item)
                }
              />
            ))}
          </div>
        </div>
        <h4>{translate('nutritionalReason.comments')}:</h4>
        <TextArea
          name="intestinal_habit_comments"
          value={storage?.nutritional_anamnese?.intestinal_habit_comments}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) =>
            handleChangeText('intestinal_habit_comments', e)
          }
          wrap="hard"
        />
        <h3>{translate('nutritionalReason.sleep')}</h3>
        <div style={{ marginTop: '10px' }}>
          <div style={{ marginBottom: '1rem' }}>
            {updatedSleep.map((item) => (
              <Checkbox
                key={`inline-${item}`}
                label={item}
                name="sleep"
                id={`inline-sleep-${item}`}
                checked={storage?.nutritional_anamnese?.sleep?.includes(item)}
                onChange={(e) => handleChange(e.target.name, item)}
              />
            ))}
          </div>
        </div>
        <h4>{translate('nutritionalReason.comments')}:</h4>
        <TextArea
          name="sleep_comments"
          value={storage?.nutritional_anamnese?.sleep_comments}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('sleep_comments', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.foodAllergies')}</h4>
        <div style={{ marginTop: '10px', marginBottom: '10px' }}>
          <Radio
            label="Sim"
            name="allergies"
            checked={storage?.nutritional_anamnese?.allergies === 'Sim'}
            onChange={() => handleChangeCheckBox('allergies', 'Sim')}
          />
          <Radio
            label="Não"
            name="allergies"
            checked={storage?.nutritional_anamnese?.allergies === 'Não'}
            onChange={() => handleChangeCheckBox('allergies', 'Não')}
          />
        </div>
        <h4>{translate('nutritionalReason.which')}:</h4>
        <TextArea
          name="allergies_comments"
          value={storage?.nutritional_anamnese?.allergies_comments}
          placeholder="Ex: Gluten, Lactose (separados por vírgula)"
          onChange={(e: any) => handleChangeText('allergies_comments', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.foodAllergiesOrIntolerance')}</h4>
        <div style={{ marginTop: '10px', marginBottom: '10px' }}>
          <Radio
            label="Sim"
            name="intolerances"
            checked={storage?.nutritional_anamnese?.intolerances === 'Sim'}
            onChange={() => handleChangeCheckBox('intolerances', 'Sim')}
          />
          <Radio
            label="Não"
            name="intolerances"
            checked={storage?.nutritional_anamnese?.intolerances === 'Não'}
            onChange={() => handleChangeCheckBox('intolerances', 'Não')}
          />
        </div>
        <h4>{translate('nutritionalReason.which')}:</h4>
        <TextArea
          name="intolerances_comments"
          value={storage?.nutritional_anamnese?.intolerances_comments}
          placeholder="Ex: Lactose, Frutose (separados por vírgula)"
          onChange={(e: any) => handleChangeText('intolerances_comments', e)}
          wrap="hard"
        />
        <h3>
          {translate('nutritionalReason.youAre')}{' '}
          <span style={{ color: '#FF7A00' }}>*</span>
        </h3>
        <div style={{ marginTop: '10px' }}>
          <div style={{ marginBottom: '1rem' }}>
            {youAre?.map((item) => (
              <Radio
                key={`inline-${item}`}
                label={item}
                name="diet"
                id={`inline-diet-${item}`}
                checked={storage?.nutritional_anamnese?.diet === item}
                onChange={() => handleChangeCheckBox('diet', item)}
              />
            ))}
          </div>
        </div>
        <h4>{translate('nutritionalReason.comments')}:</h4>
        <TextArea
          name="diet_comments"
          value={storage?.nutritional_anamnese?.diet_comments}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('diet_comments', e)}
          wrap="hard"
        />
        <h3>{translate('nutritionalReason.foodRoutine')}</h3>
        <h4>{translate('nutritionalReason.breakfast')}:</h4>
        <TextArea
          name="routine_breakfast"
          value={storage?.nutritional_anamnese?.routine_breakfast}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('routine_breakfast', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.morningSnack')}:</h4>
        <TextArea
          name="routine_morning_snack"
          value={storage?.nutritional_anamnese?.routine_morning_snack}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('routine_morning_snack', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.lunch')}:</h4>
        <TextArea
          name="routine_lunch"
          value={storage?.nutritional_anamnese?.routine_lunch}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('routine_lunch', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.afternoonSnack')}:</h4>
        <TextArea
          name="routine_afternoon_snack"
          value={storage?.nutritional_anamnese?.routine_afternoon_snack}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('routine_afternoon_snack', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.dinner')}:</h4>
        <TextArea
          name="routine_dinner"
          value={storage?.nutritional_anamnese?.routine_dinner}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('routine_dinner', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.supper')}:</h4>
        <TextArea
          name="routine_supper"
          value={storage?.nutritional_anamnese?.routine_supper}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('routine_supper', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.weekends')}:</h4>
        <TextArea
          name="routine_weekends"
          value={storage?.nutritional_anamnese?.routine_weekends}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('routine_weekends', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.anyFoodAversions')}</h4>
        <TextArea
          name="food_aversion"
          value={storage?.nutritional_anamnese?.food_aversion}
          placeholder="Ex: Morango, Pão, Farinha (separados por vírgula)"
          onChange={(e: any) => handleChangeText('food_aversion', e)}
          wrap="hard"
        />
        <h4>Preferências alimentares:</h4>
        <TextArea
          name="preferences"
          value={storage?.nutritional_anamnese?.preferences}
          placeholder="Ex: Opções salgadas, Vitaminas com whey, Sanduíche natural (separados por vírgula)"
          onChange={(e: any) => handleChangeText('preferences', e)}
          wrap="hard"
        />
        <h4>{translate('nutritionalReason.anyConsiderations')}</h4>
        <TextArea
          name="considerations"
          value={storage?.nutritional_anamnese?.considerations}
          placeholder={translate('medicalRecord.describe')}
          onChange={(e: any) => handleChangeText('considerations', e)}
          wrap="hard"
        />
        <h4>Informações extras:</h4>
        <TextArea
          name="extra_information"
          value={storage?.nutritional_anamnese?.extra_information}
          placeholder="Descreva aqui"
          onChange={(e: any) => handleChangeText('extra_information', e)}
          wrap="hard"
        />

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            disabled={!hasFilledAnamnese || isGeneratingPlan || !!planSent}
            onClick={generateMealPlan}
            style={{
              backgroundColor: '#FF7A00',
              color: '#fff',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '6px',
              cursor:
                hasFilledAnamnese && !isGeneratingPlan && !planSent
                  ? 'pointer'
                  : 'not-allowed',
              opacity:
                hasFilledAnamnese && !isGeneratingPlan && !planSent ? 1 : 0.6,
              marginTop: '20px',
            }}
          >
            {isGeneratingPlan
              ? 'Gerando plano...'
              : planSent
                ? 'Plano já enviado'
                : 'Gerar plano alimentar'}
          </button>
        </div>
      </S.Div>
    </S.SectionContent>
  )
}

export default AnamneseNutri
